import { GetExpenseResponse, GetPayUpType } from '@orbit/api-types'
import { useMemo } from 'react'
import { ActivityFilter, isFilterActive } from './ActivityFilterModal'
import { ActivityItem, formatDateKey } from './balancesFormat'

export type ActivityDateGroup = { dateKey: string; items: ActivityItem[] }

const matchesExpense = (
    expense: GetExpenseResponse,
    paidBySet: Set<number>,
    owesSet: Set<number>,
): boolean => {
    if (paidBySet.has(expense.paidByUserId)) return true
    if (owesSet.size > 0) {
        for (const p of expense.participants) {
            if (owesSet.has(p.userId)) return true
        }
    }
    return false
}

const matchesPayUp = (payUp: GetPayUpType, payUpSet: Set<number>): boolean => {
    return payUpSet.has(payUp.fromUserId) || payUpSet.has(payUp.toUserId)
}

export const useGroupedActivity = (
	expenses: GetExpenseResponse[],
	payUps: GetPayUpType[],
	activityFilter: ActivityFilter,
): ActivityDateGroup[] => {
	return useMemo(() => {
        const active = isFilterActive(activityFilter)
        const paidBySet = new Set(activityFilter.paidByUserIds)
        const owesSet = new Set(activityFilter.owesUserIds)
        const payUpSet = new Set(activityFilter.payUpUserIds)

		const items: ActivityItem[] = [
			...expenses.map<ActivityItem>(e => ({
				kind: 'expense',
				date: e.expenseDate,
				data: e,
			})),
			...payUps.map<ActivityItem>(p => ({
				kind: 'payup',
				date: p.payUpDate,
				data: p,
			})),
		]
			.filter(item => {
                if (!active) return true
                if (item.kind === 'expense') return matchesExpense(item.data, paidBySet, owesSet)
                return matchesPayUp(item.data, payUpSet)
            })
			.sort((a, b) => b.date - a.date)

		const groups: ActivityDateGroup[] = []
		const indexByKey = new Map<string, number>()
		for (const item of items) {
			const key = formatDateKey(item.date)
			const existing = indexByKey.get(key)
			if (existing === undefined) {
				indexByKey.set(key, groups.length)
				groups.push({ dateKey: key, items: [item] })
			} else {
				groups[existing].items.push(item)
			}
		}
		return groups
	}, [expenses, payUps, activityFilter])
}
